import { AnimatePresence, motion } from 'framer-motion';
import { isUndefined } from 'lodash';
import React, { ReactElement, ReactNode, useEffect, useRef } from 'react';
import cn from 'classnames';
import MUIModal from '@mui/material/Modal';
import { ModalStyleEnum } from '@/shared/enums/Modal';
import useModalContext from '@/shared/hooks/useModalContext';

type Props = {
    isOpen : boolean;
    onOpen ?: (modalRef : null | HTMLDivElement) => void;
    onClose ?: (modalRef : null | HTMLDivElement) => void;
    style ?: ModalStyleEnum,
    children : ReactNode;
    className ?: null | string;
    closeModal : () => void;
    modalClassName ?: null | string;
    modalInnerClassName ?: null | string;
};

const Modal : React.FC<Props> = ({
    isOpen, onOpen, onClose, style = ModalStyleEnum.Normal, children, className = '', closeModal, modalClassName = '',
    modalInnerClassName = '',
}) : ReactElement => {
    const modalRef = useRef<HTMLDivElement>(null);
    const previousFocusRef = useRef<HTMLElement | null>(null);
    const { isModalLocked } = useModalContext();
    const lockedRef = useRef(isModalLocked);
    lockedRef.current = isModalLocked;

    useEffect(() : void => {
        if (isOpen && !isUndefined(onOpen)) {
            onOpen(modalRef.current);
        } else if (!isOpen && !isUndefined(onClose)) {
            onClose(modalRef.current);
        }
    }, [isOpen]);

    useEffect(() => {
        let focusFrame : number | null = null;

        const handleKeyDown = (event : KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                handleClose();
            }

            if (event.key === 'Tab' && isOpen && modalRef.current) {
                const focusable = Array.from(modalRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'));

                if (focusable.length === 0) {
                    event.preventDefault();
                    modalRef.current.focus({ preventScroll: true });

                    return;
                }

                const first = focusable[0];
                const last = focusable[focusable.length - 1];

                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault();
                    last.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault();
                    first.focus();
                }
            }
        };

        if (isOpen) {
            previousFocusRef.current = document.activeElement as HTMLElement | null;
            document.addEventListener('keydown', handleKeyDown);
            focusFrame = window.requestAnimationFrame(() => {
                const firstFocusable = modalRef.current?.querySelector<HTMLElement>('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');

                (firstFocusable ?? modalRef.current)?.focus({ preventScroll: true });
            });
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);

            if (focusFrame !== null) window.cancelAnimationFrame(focusFrame);
            previousFocusRef.current?.focus({ preventScroll: true });
        };
    }, [isOpen]);

    const handleClose = () => {
        if (lockedRef.current) return;

        const inputs = Array.from(modalRef.current?.querySelectorAll('input, textarea') ?? []);
        const inputCount = new Set(inputs.map((input, index) =>
            input instanceof HTMLInputElement && input.type === 'radio' && input.name ? `radio:${input.name}` : index,
        )).size;

        if (inputCount <= 1) {
            closeModal();
        }
    };

    return (
        <MUIModal open={isOpen} onClose={handleClose} hideBackdrop disableAutoFocus disableEnforceFocus disableRestoreFocus>
            <div><AnimatePresence>
            {
                isOpen && (
                    <motion.div
                        initial={false}
                        animate={isOpen ? 'open' : 'closed'}
                        className={cn(
                            'w-screen h-screen flex items-center justify-center fixed top-0 left-0 z-[1300] bg-secondary-800/30',
                            className,
                        )}
                        style={{ WebkitTransform: 'translate3d(0,0,0)' }}
                        onClick={() => handleClose()}
                    >
                        <motion.div
                            ref={modalRef}
                            role='dialog'
                            aria-modal='true'
                            aria-labelledby='modal-modal-title'
                            tabIndex={-1}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'tween', duration: 0.15 }}
                            exit={{ scale: 0 }}
                            className={cn(
                                'z-[1400] max-h-[calc(100dvh-1rem)] max-w-[calc(100vw-1rem)] overflow-y-auto relative shadow-xl drop-shadow-md sm:max-h-[92vh] md:max-w-[90vw]',
                                {
                                    'w-[calc(100vw-1rem)] bg-white dark:bg-secondary-600 rounded-2xl px-4 pt-4 pb-6 sm:w-fit sm:rounded-3xl sm:px-[1.75rem] sm:pt-[1.75rem] sm:pb-[2.25rem]': style === ModalStyleEnum.Normal,
                                    'bg-warning-400 rounded-2xl p-6 max-w-[360px]': style === ModalStyleEnum.Alert,
                                },
                                className,
                                modalInnerClassName,
                            )}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {children}
                        </motion.div>
                    </motion.div>
                )
            }
        </AnimatePresence></div>
        </MUIModal>
    );
};

export default Modal;
